import { APILookupValue, APIObject } from "@/services/api/types";
import { IcpFit, IcpMetadata } from "@/entities/icpFit/types";

export const UNKNOWN_PERSON_ID = "p-unknown";
export const NONE_PERSON_ID = "p-none";
export const OTHER_PERSON_ID = "p-other";

// Sentinel IDs are FK integrity placeholders, not real people. They must never
// be opened in an editable detail view or sent to mutating endpoints (CRM-6081).
export const RESERVED_PERSON_IDS: readonly string[] = [
  UNKNOWN_PERSON_ID,
  NONE_PERSON_ID,
  OTHER_PERSON_ID
];

export const getPersonFixedOptions = (t: (key: string) => string) => [
  { id: UNKNOWN_PERSON_ID, displayName: t("people.unknown") },
  { id: NONE_PERSON_ID, displayName: t("people.none") }
];

export const isReservedPersonId = (id: string | null | undefined): boolean => {
  if (id == null) return false;
  return RESERVED_PERSON_IDS.includes(id);
};

export enum PeopleEntityView {
  COMPANY = "company",
  PARTNER = "partner"
}

export const PEOPLE_VIEW_PARAM = "view";

export enum PersonColumn {
  FULL_NAME = "fullName",
  COMPANY_NAME = "companyName",
  STATUS = "status",
  REPORTS_TO = "reportsTo",
  JOB_TITLE = "jobTitle",
  JOB_FUNCTION = "jobFunction",
  PHONE = "phone",
  DESK_PHONE = "deskPhone",
  ADDRESS = "address",
  COUNTRY = "country",
  EMAIL = "email",
  CREATED_AT = "createdAt",
  STATUS_CHANGED_AT = "statusChangedAt",
  REFERENCEABILITY = "referenceability"
}

export enum PersonReferenceabilityLevel {
  UNKNOWN = 1,
  UNLIKELY_TO_ENDORSE,
  WEAK_ADVOCATE,
  STRONG_ADVOCATE
}

export enum PersonStatus {
  NEW = 1,
  GREENFIELD,
  ENGAGED_IN_DEAL,
  ENGAGED_IN_LEAD,
  ENGAGED,
  ACTIVE_CUSTOMER,
  DISQUALIFIED
}

export interface PersonJobFunctionsResponse {
  jobFunctions: APILookupValue[];
}

interface PersonData extends APIObject {
  firstName: string;
  lastName: string;
  companyId: string;
  companyName?: string;
  jobTitle: string | null;
  phone: string | null;
  email: string | null;
  emails: string[] | null;
  createdAt: string | null;
  referenceability?: number | null;
  jobFunction?: string | null;
  deskPhone?: string | null;
  reportsTo?: string | null;
  address?: string | null;
  country?: string | null;
  notes?: string | null;
  status?: number | null;
  statusChangedAt?: string | null;
  statusChangedBy?: string | null;
  statusReasonText?: string | null;
  activeDealId?: string | null;
  activeLeadId?: string | null;
  reportsToFirstName?: string;
  reportsToLastName?: string;
  icpFit?: IcpFit | null;
  icpMetadata?: IcpMetadata | null;
  lastActivity?: string | null;
}

interface PeoplePage {
  people: PersonData[];
  total: number;
  page: number;
  size: number;
  pages: number;
}

export enum PeopleView {
  TABLE = 1,
  CARDS
}

export type { PersonData, PeoplePage };
